// Platform admin: the owner's control tower. Six-KPI header, tabbed body
// (Centers · Demos · Sign-ins · Devices · Send Invite), each backed by a
// focused subcomponent under /components/admin.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Copy, Link2, Check, Activity as ActivityIcon, DollarSign, Mail } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

import AdminKpis from '@/components/admin/AdminKpis';
import AdminTabs from '@/components/admin/AdminTabs';
import CentersTab from '@/components/admin/CentersTab';
import DemosTab from '@/components/admin/DemosTab';
import SignInsTab from '@/components/admin/SignInsTab';
import DevicesTab from '@/components/admin/DevicesTab';

// ─── Send Invite (prospect demo links) tab — kept from the old admin ──────────
function SendInviteTab() {
  const [demoLinks, setDemoLinks] = useState([]);
  const [demoLabel, setDemoLabel] = useState('');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const load = useCallback(async () => {
    try { const res = await api.adminDemoLinks(); setDemoLinks(res.data.demo_links); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!demoLabel.trim()) return;
    try { await api.adminCreateDemoLink({ label: demoLabel.trim() }); setDemoLabel(''); await load(); toast.success('Demo link created.'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed.'); }
  };

  const expiresIn = (iso) => {
    if (!iso) return '';
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return 'expired';
    const h = Math.round(ms / 3600000);
    return h >= 24 ? `in ${Math.round(h / 24)}d` : `in ${h}h`;
  };

  return (
    <section className="mt-6" data-testid="invite-tab">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-slate-700" />
          <h3 className="font-display text-base font-semibold text-slate-900">Prospect demo links</h3>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          One-off 72-hour access links to send prospects who want to explore. Each link opens the full product,
          pre-filled with sample data, and expires by itself. For full onboarding (real students, real reports),
          create a demo <b>account</b> under the Demos tab instead.
        </p>
        <form onSubmit={create} className="mt-4 flex items-center gap-2" data-testid="prospect-link-form">
          <Input placeholder="Prospect or company" value={demoLabel} onChange={(e) => setDemoLabel(e.target.value)} className="h-10 w-72" data-testid="prospect-link-label" />
          <Button type="submit" size="sm" className="cta">
            <Link2 className="h-3.5 w-3.5" /> Create link (72h)
          </Button>
        </form>
      </div>

      {demoLinks.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500">No prospect links yet.</p>
      ) : (
        <div className="mt-4 divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
          {demoLinks.map((link) => (
            <div key={link.token} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5">
              <p className="min-w-0 flex-1 font-display text-[15px] font-medium text-slate-900">{link.label}</p>
              <span className="text-xs text-slate-400">expires {expiresIn(link.expires_at)} · {link.uses} use{link.uses === 1 ? '' : 's'}</span>
              <CopyLink text={`${origin}/demo/${link.token}`} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CopyLink({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={async () => { try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1200); } catch { /* noop */ } }}
      className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-slate-300">
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

// ─── Small link-out card (for Activity / AI spend / Security) ─────────────────
function LinkOutCard({ to, icon: Icon, title, sub }) {
  return (
    <Link to={to} className="group flex items-center justify-between rounded-lg border border-slate-200 bg-white px-5 py-4 hover:border-slate-400">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-slate-100 text-slate-700"><Icon className="h-4 w-4" /></div>
        <div><p className="font-display text-[15px] font-semibold text-slate-900">{title}</p><p className="text-xs text-slate-500">{sub}</p></div>
      </div>
      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-slate-800" />
    </Link>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function AdminPortal() {
  const [tab, setTab] = useState('centers');
  const [kpis, setKpis] = useState(null);
  const [alertBadges, setAlertBadges] = useState({});

  const loadKpis = useCallback(async () => {
    try { const res = await api.adminKpis(); setKpis(res.data); } catch { /* silent */ }
  }, []);
  const loadAlerts = useCallback(async () => {
    try {
      const res = await api.adminDeviceAlerts();
      setAlertBadges((b) => ({ ...b, devices: res.data.total_flagged || 0 }));
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadKpis(); loadAlerts(); }, [loadKpis, loadAlerts]);

  const onChanged = () => { loadKpis(); loadAlerts(); };

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl">
        <div className="border-b border-slate-200 pb-5">
          <p className="mono-stat">Platform</p>
          <h1 className="h-display mt-1 text-3xl sm:text-4xl">Platform administration</h1>
          <p className="mt-1 text-sm text-slate-500">Manage all centres and onboard new ones.</p>
        </div>
        <AdminKpis data={kpis} />
        <AdminTabs active={tab} onChange={setTab} badges={alertBadges} />

        {tab === 'centers'  && <CentersTab onChanged={onChanged} />}
        {tab === 'demos'    && <DemosTab   onChanged={onChanged} />}
        {tab === 'signins'  && <SignInsTab />}
        {tab === 'devices'  && <DevicesTab />}
        {tab === 'invite'   && <SendInviteTab />}
        {tab === 'activity' && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <LinkOutCard to="/admin/activity" icon={ActivityIcon} title="Site activity" sub="Content usage, page hits, per-user engagement" />
            <LinkOutCard to="/admin/ops"      icon={ActivityIcon} title="Content & scoring health" sub="Inventory, submissions, health checks" />
          </div>
        )}
        {tab === 'spend'    && (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <LinkOutCard to="/admin/spend" icon={DollarSign} title="AI spend" sub="Per-org and per-task LLM cost" />
          </div>
        )}
        {tab === 'security' && (
          <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600">
            <p>Security overview lives inside <b>Sign-ins</b> (auth log) and <b>Devices</b> (shared-account signals) tabs.
              A dedicated summary is coming with route access log and tracked links.</p>
          </div>
        )}
      </div>
    </AppShell>
  );
}
